import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { SquarePen } from 'lucide-react'
import type { ConversationSummary } from './api'
import { relativeTime } from '@tendbot/ui'
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@tendbot/ui'

interface ConversationDrawerProps {
  open: boolean
  conversations: ConversationSummary[]
  currentConversationId: string | null
  onClose: () => void
  onSelectConversation: (id: string) => void
  onNewChat: () => void
  loading: boolean
}

export const ConversationDrawer = memo(function ConversationDrawer({
  open,
  conversations,
  currentConversationId,
  onClose,
  onSelectConversation,
  onNewChat,
  loading,
}: ConversationDrawerProps) {
  const { t } = useTranslation('chat')

  const handleSelect = (id: string) => {
    onSelectConversation(id)
    onClose()
  }

  const handleNewChat = () => {
    onNewChat()
    onClose()
  }

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <SheetContent
        side="left"
        showCloseButton={false}
        className="w-[85vw] max-w-sm pt-[env(safe-area-inset-top)] lg:hidden"
      >
        <SheetTitle className="sr-only">{t('sidebar.newChat')}</SheetTitle>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gold/10">
          <button
            onClick={handleNewChat}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-gold text-sm font-medium hover:bg-gold/10 transition-colors"
          >
            <SquarePen size={16} />
            {t('sidebar.newChat')}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-14 mb-1 rounded-lg bg-white/[0.03] animate-pulse" />
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <p className="px-4 py-4 text-pearl/55 text-sm text-center">
              {t('sidebar.noConversations')}
            </p>
          ) : (
            <div className="py-1">
              {conversations.map((conv, idx) => {
                const isCurrent = conv.id === currentConversationId
                const isActive = conv.isActive
                const showArchiveDivider = !isActive && idx > 0 && conversations[idx - 1]?.isActive

                return (
                  <div key={conv.id}>
                    {showArchiveDivider && (
                      <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                        <div className="flex-1 h-px bg-pearl/8" />
                        <span className="text-[10px] uppercase tracking-widest text-pearl/45 font-medium">{t('sidebar.archiveDivider')}</span>
                        <div className="flex-1 h-px bg-pearl/8" />
                      </div>
                    )}
                    <button
                      onClick={() => handleSelect(conv.id)}
                      className={`w-full text-left px-4 py-3 transition-all duration-200 border-l-2 ${
                        isCurrent && isActive
                          ? 'bg-gold/10 border-gold text-ivory shadow-[inset_0_0_20px_rgba(200,149,48,0.06)]'
                          : isCurrent
                            ? 'bg-white/[0.06] border-pearl/30 text-pearl/70'
                            : isActive
                              ? 'border-gold/30 text-ivory/80 hover:bg-gold/5 hover:text-ivory'
                              : 'border-transparent text-pearl/55 hover:bg-white/[0.03] hover:text-pearl/75'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <p className={`text-sm leading-5 line-clamp-2 flex-1 ${!isActive && !isCurrent ? 'font-normal' : 'font-medium'}`}>
                          {conv.title || conv.lastMessage || '\u00A0'}
                        </p>
                        {isActive && (
                          <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-gold shadow-[0_0_6px_rgba(200,149,48,0.5)]" />
                        )}
                      </div>
                      <p className={`text-[11px] mt-0.5 ${isActive ? 'text-gold/50' : 'opacity-30'}`}>
                        {relativeTime(conv.updatedAt)}
                      </p>
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
})
